<script setup lang="ts">
import type { EventPlayer } from "#components";
import type { IPlayerState } from "@pixellot/vue-player/src/util/state";
import type { VideoJsPlayer as IPixellotPlayer } from "@pixellot/web-sdk";
import type { IPlayerData } from "../Player/index.vue";
import Cookies from "js-cookie";
import { EVENT_STATUS } from "~/constants";
import useEventStore from "~/stores/event";

export interface EventInfoProps {
  event: IModelEvent;
}

const props = defineProps<EventInfoProps>();
const route = useRoute();

const eventStore = useEventStore();
const videoPlayerRef = ref<InstanceType<typeof EventPlayer> | null>(null);
const player = computed<IPixellotPlayer | null>(() => videoPlayerRef.value ? videoPlayerRef.value?.player : null);
const playerState = computed<IPlayerState | null>(() => videoPlayerRef.value ? videoPlayerRef.value?.state : null);

const tags = computed(() => [...eventStore.tags.data.values()]);
const isLive = computed(() => props.event.eventStatus === EVENT_STATUS.LIVE);
const isUpcoming = computed(() => props.event.eventStatus === EVENT_STATUS.UPCOMING);
const eventIsNotAvailableForUser = computed(() => !props.event.isEventAvailableForUser);
const eventIsNotAvailableUserRegion = computed(() => !props.event.isEventAvailableInUserRegion);
const isEventStartingSoon = computed(() => eventStore.isEventStartingSoon);
const hasPlayerError = computed(() => eventStore.playerError);

const isShowingPoster = computed(() => {
  if (eventIsNotAvailableUserRegion.value || eventIsNotAvailableForUser.value)
    return true;

  if (Cookies.get(`streamError_${eventStore.data?.id}`) === "true")
    return false;

  if (isEventStartingSoon.value)
    return !hasPlayerError.value;

  return isUpcoming.value;
});

const onPlayerLoaded = ({ player }: IPlayerData) => {
  if (route.query.time) {
    nextTick(() => {
      player.currentTime(Number(route.query.time));
    });
  }
};

onMounted(() => {
  if (isLive.value && !props.event.source.hd)
    eventStore.isEventStartingSoon = true;
});

watch(() => videoPlayerRef.value, () => {
  eventStore.player = player.value;
  eventStore.playerState = playerState.value;
});

// TODO: Show player for 'live' event when no blocks
</script>

<template>
  <EventPoster
    v-if="isShowingPoster"
    v-bind="props.event"
  />
  <ClientOnly v-else>
    <Suspense>
      <template #fallback>
        <div class="skeletonLoading aspect-video" />
      </template>
      <LazyEventPlayer
        ref="videoPlayerRef"
        :event="props.event"
        :tags="tags"
        @mounted="onPlayerLoaded"
      />
    </Suspense>
    <div id="player-bottom" />
  </ClientOnly>
</template>
